(function () {
  const ACCEPT_BY_TYPE = {
    PNG: "image/png",
    JPG: "image/jpeg",
    PDF: "application/pdf",
  };

  function endpoint(widget, path) {
    const appUrl = widget.dataset.appUrl && widget.dataset.appUrl.replace(/\/$/, "");
    return appUrl ? `${appUrl}/apps/printready/${path}` : `/apps/printready/${path}`;
  }

  function productForm(widget) {
    return (
      widget.closest("form[data-type='add-to-cart-form']") ||
      document.querySelector("form[data-type='add-to-cart-form']") ||
      widget.closest("form[action*='/cart/add']") ||
      Array.from(document.querySelectorAll("form[action*='/cart/add']")).find((form) => form.querySelector("button[type='submit'], input[type='submit']"))
    );
  }

  function setButtons(form, disabled) {
    if (!form) return;
    form.querySelectorAll("button[type='submit'], input[type='submit']").forEach((button) => {
      button.disabled = disabled;
      button.setAttribute("aria-disabled", disabled ? "true" : "false");
    });
  }

  function setMessage(widget, message, isError) {
    const messageNode = widget.querySelector("[data-printready-message]");
    widget.classList.toggle("printready-widget--error", Boolean(isError));
    messageNode.textContent = message || "";
  }

  function extensionForFile(file) {
    const name = file.name.toLowerCase();
    if (file.type === "image/png" || name.endsWith(".png")) return "PNG";
    if (file.type === "image/jpeg" || name.endsWith(".jpg") || name.endsWith(".jpeg")) return "JPG";
    if (file.type === "application/pdf" || name.endsWith(".pdf")) return "PDF";
    return "";
  }

  function readImageDimensions(file) {
    return new Promise((resolve, reject) => {
      const image = new Image();
      const objectUrl = URL.createObjectURL(file);

      image.onload = function () {
        URL.revokeObjectURL(objectUrl);
        resolve({ width: image.naturalWidth, height: image.naturalHeight });
      };

      image.onerror = function () {
        URL.revokeObjectURL(objectUrl);
        reject(new Error("Could not read image dimensions."));
      };

      image.src = objectUrl;
    });
  }

  function upsertProperty(form, name, value) {
    let input = form.querySelector(`input[name="properties[${name}]"]`);
    if (!input) {
      input = document.createElement("input");
      input.type = "hidden";
      input.name = `properties[${name}]`;
      form.appendChild(input);
    }
    input.value = value || "";
  }

  function removeProperty(form, name) {
    const input = form?.querySelector(`input[name="properties[${name}]"]`);
    if (input) input.remove();
  }

  function clearProperties(form) {
    ["PrintReady file", "PrintReady filename", "PrintReady dimensions", "PrintReady status"].forEach((name) => {
      const input = form.querySelector(`input[name="properties[${name}]"]`);
      if (input) input.remove();
    });
  }

  async function validateFile(file, rule) {
    const extension = extensionForFile(file);

    if (!extension || !rule.allowedFileTypes.includes(extension)) {
      throw new Error(`Allowed file types: ${rule.allowedFileTypes.join(", ")}.`);
    }

    if (file.size > rule.maxFileSizeMb * 1024 * 1024) {
      throw new Error(`File must be ${rule.maxFileSizeMb} MB or smaller.`);
    }

    if (extension === "PDF") {
      return {
        extension,
        width: null,
        height: null,
        validationStatus: "valid_pdf_pending_dimensions",
        dimensionsLabel: "PDF dimensions will be checked after order",
      };
    }

    const dimensions = await readImageDimensions(file);
    if (dimensions.width !== rule.requiredWidth || dimensions.height !== rule.requiredHeight) {
      throw new Error(`Image must be exactly ${rule.requiredWidth} x ${rule.requiredHeight}px.`);
    }

    return {
      extension,
      width: dimensions.width,
      height: dimensions.height,
      validationStatus: "valid",
      dimensionsLabel: `${dimensions.width} x ${dimensions.height}px`,
    };
  }

  async function uploadFile(widget, file, rule, clientValidation) {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("productId", widget.dataset.productId);
    formData.append("productTitle", widget.dataset.productTitle || "");
    formData.append("tags", widget.dataset.productTags || "");
    formData.append("shop", widget.dataset.shop);

    const response = await fetch(endpoint(widget, "upload"), {
      method: "POST",
      body: formData,
    });
    const payload = await response.json();

    if (!response.ok || !payload.ok) {
      throw new Error(payload.error || "Upload failed.");
    }

    return {
      ...payload.upload,
      width: payload.upload.width || clientValidation.width,
      height: payload.upload.height || clientValidation.height,
      dimensionsLabel: payload.upload.dimensionsLabel || clientValidation.dimensionsLabel,
    };
  }

  async function initWidget(widget) {
    if (widget.dataset.printreadyInitialized) return;
    widget.dataset.printreadyInitialized = "true";

    const form = productForm(widget);
    const panel = widget.querySelector(".printready-widget__panel");
    const requirements = widget.querySelector("[data-printready-requirements]");
    const helper = widget.querySelector("[data-printready-helper]");
    const status = widget.querySelector("[data-printready-status]");
    const input = widget.querySelector("[data-printready-file]");
    const uploadField = widget.querySelector("[data-printready-upload-field]");
    const nameField = widget.querySelector("[data-printready-name-field]");
    const nameInput = widget.querySelector("[data-printready-name]");
    const nameLabel = widget.querySelector("[data-printready-name-label]");

    const rulesUrl = new URL(endpoint(widget, "rules"), window.location.origin);
    rulesUrl.searchParams.set("product_id", widget.dataset.productId);
    rulesUrl.searchParams.set("shop", widget.dataset.shop);
    if (widget.dataset.productTags) {
      rulesUrl.searchParams.set("tags", widget.dataset.productTags);
    }

    const response = await fetch(rulesUrl.toString());
    const payload = await response.json();
    const rule = payload.rule;

    if (!rule) return;

    panel.hidden = false;
    const ruleType = rule.ruleType || "artwork";
    helper.textContent = rule.helperText || "";
    setButtons(form, true);

    form?.addEventListener("submit", function (event) {
      if (widget.dataset.printreadyValid !== "true") {
        event.preventDefault();
        setMessage(
          widget,
          ruleType === "name"
            ? `Enter ${rule.inputLabel || "Name"} before adding this item to cart.`
            : "Upload valid print-ready artwork before adding this item to cart.",
          true,
        );
      }
    });

    if (ruleType === "name") {
      const propertyName = rule.inputLabel || "Name";
      uploadField.hidden = true;
      nameField.hidden = false;
      nameLabel.textContent = propertyName;
      requirements.textContent = `${propertyName} required for this product.`;

      nameInput.addEventListener("input", function () {
        const value = nameInput.value.trim();
        widget.dataset.printreadyValid = value ? "true" : "false";
        widget.classList.toggle("printready-widget--valid", Boolean(value));
        status.textContent = value ? "Ready" : "Required";
        removeProperty(form, propertyName);

        if (!value) {
          setMessage(widget, `Enter ${propertyName}.`, true);
          setButtons(form, true);
          return;
        }

        upsertProperty(form, propertyName, value);
        setMessage(widget, `${propertyName} added.`, false);
        setButtons(form, false);
      });

      return;
    }

    nameField.hidden = true;
    uploadField.hidden = false;
    input.accept = rule.allowedFileTypes.map((type) => ACCEPT_BY_TYPE[type]).filter(Boolean).join(",");
    requirements.textContent = `${rule.requiredWidth} x ${rule.requiredHeight}px. Allowed: ${rule.allowedFileTypes.join(", ")}. Max ${rule.maxFileSizeMb} MB.`;

    input.addEventListener("change", async function () {
      const file = input.files && input.files[0];
      widget.dataset.printreadyValid = "false";
      widget.classList.remove("printready-widget--valid");
      clearProperties(form);
      setButtons(form, true);

      if (!file) {
        setMessage(widget, "Choose an artwork file.", true);
        return;
      }

      try {
        setMessage(widget, "Checking file...", false);
        const clientValidation = await validateFile(file, rule);
        setMessage(widget, clientValidation.dimensionsLabel, false);

        const upload = await uploadFile(widget, file, rule, clientValidation);
        upsertProperty(form, "PrintReady file", upload.url);
        upsertProperty(form, "PrintReady filename", upload.originalFilename);
        upsertProperty(form, "PrintReady dimensions", upload.dimensionsLabel);
        upsertProperty(form, "PrintReady status", upload.validationStatus);

        widget.dataset.printreadyValid = "true";
        widget.classList.add("printready-widget--valid");
        status.textContent = "Ready";
        setMessage(widget, upload.dimensionsLabel === "PDF pending dimensions" ? "PDF dimensions will be checked after order." : "Artwork uploaded and ready.", false);
        setButtons(form, false);
      } catch (error) {
        status.textContent = "Required";
        setMessage(widget, error.message, true);
      }
    });
  }

  function widgetMarkup(anchor) {
    const escapeAttribute = (value) =>
      String(value || "")
        .replace(/&/g, "&amp;")
        .replace(/"/g, "&quot;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");

    return `
      <div
        class="printready-widget"
        data-printready-widget
        data-app-url="${escapeAttribute(anchor.dataset.appUrl)}"
        data-product-id="${escapeAttribute(anchor.dataset.productId)}"
        data-product-title="${escapeAttribute(anchor.dataset.productTitle)}"
        data-product-tags="${escapeAttribute(anchor.dataset.productTags)}"
        data-shop="${escapeAttribute(anchor.dataset.shop)}"
      >
        <div class="printready-widget__panel" hidden>
          <div class="printready-widget__header">
            <strong>Print-ready artwork</strong>
            <span data-printready-status>Required</span>
          </div>
          <p class="printready-widget__requirements" data-printready-requirements></p>
          <p class="printready-widget__helper" data-printready-helper></p>
          <label class="printready-widget__input" data-printready-upload-field>
            <span>Upload artwork</span>
            <input data-printready-file type="file">
          </label>
          <label class="printready-widget__input" data-printready-name-field hidden>
            <span data-printready-name-label>Name</span>
            <input data-printready-name type="text" maxlength="80" autocomplete="off">
          </label>
          <p class="printready-widget__message" data-printready-message></p>
        </div>
      </div>
    `;
  }

  async function hydrateAutoAnchor(anchor) {
    if (anchor.dataset.productId) return true;

    const metaProduct = window.ShopifyAnalytics?.meta?.product;
    const productPath = window.location.pathname.replace(/\/$/, "");
    if (!metaProduct?.id || !productPath) return false;

    anchor.dataset.productId = String(metaProduct.id);
    anchor.dataset.productTitle = metaProduct.handle || "";

    try {
      const response = await fetch(`${productPath}.js`);
      const product = await response.json();
      anchor.dataset.productTitle = product.title || anchor.dataset.productTitle || "";
      anchor.dataset.productTags = Array.isArray(product.tags) ? product.tags.join(",") : "";
    } catch {
      anchor.dataset.productTags = "";
    }

    return true;
  }

  function initAutoAnchors() {
    document.querySelectorAll("[data-printready-auto]").forEach(async (anchor) => {
      if (document.querySelector("[data-printready-widget]")) return;

      const isReady = await hydrateAutoAnchor(anchor);
      if (!isReady || document.querySelector("[data-printready-widget]")) return;

      const form = productForm(anchor);
      if (!form) return;

      const template = document.createElement("template");
      template.innerHTML = widgetMarkup(anchor).trim();
      const widget = template.content.firstElementChild;
      const formButtons = form.querySelector(".product-form-buttons");
      form.insertBefore(widget, formButtons || form.firstChild);
      initWidget(widget);
    });
  }

  document.querySelectorAll("[data-printready-widget]").forEach(initWidget);
  initAutoAnchors();
})();
